import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import CatalogEmptyState from "../../common/catalog/CatalogEmptyState";
import DetailAccentCard from "../../common/detail/DetailAccentCard";
import { editorInputClassName } from "../../common/editor/editorStyles";
import Paginator from "../../common/paginator/Paginator";
import WebinarAdminListRow from "./WebinarAdminListRow";

export default function WebinarAdminListSection() {
	const navigate = useNavigate();
	const [webinars, setWebinars] = useState([]);
	const [listQuery, setListQuery] = useState("");
	const [appliedQuery, setAppliedQuery] = useState("");
	const [page, setPage] = useState(1);
	const [maxPageNum, setMaxPageNum] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const listSectionRef = useRef(null);

	const fetchWebinars = useCallback((currentPage = 1, currentQuery = "") => {
		setIsLoading(true);
		setErrorMessage("");
		return api
			.webinarAdminList({
				token: LoginHelper.getToken(),
				query: currentQuery,
				page: currentPage,
			})
			.then((result) => {
				setWebinars(result.webinars || []);
				setMaxPageNum(result.max_page || 1);
				if (result.page) {
					setPage(result.page);
				}
				return result;
			})
			.catch((err) => {
				setErrorMessage(err?.data?.message || "دریافت وبینارها ناموفق بود");
				return null;
			})
			.finally(() => setIsLoading(false));
	}, []);

	useEffect(() => {
		fetchWebinars(page, appliedQuery);
	}, [page, appliedQuery, fetchWebinars]);

	function applyListSearch(event) {
		event?.preventDefault?.();
		if (isLoading) return;
		const next = listQuery.trim();
		if (next !== appliedQuery) {
			setAppliedQuery(next);
			setPage(1);
			return;
		}
		fetchWebinars(page, appliedQuery);
	}

	function handleRemoved() {
		fetchWebinars(page, appliedQuery).then((result) => {
			if (
				result &&
				(result.webinars || []).length === 0 &&
				page > 1
			) {
				setPage(page - 1);
			}
		});
	}

	const listSummary =
		maxPageNum > 1
			? `صفحه ${page.toLocaleString("fa-IR")} از ${maxPageNum.toLocaleString("fa-IR")}`
			: webinars.length > 0
				? `${webinars.length.toLocaleString("fa-IR")} وبینار`
				: "۰ وبینار";

	return (
		<section className="flex flex-1 flex-col gap-6">
			<div ref={listSectionRef}>
				<DetailAccentCard bodyClassName="p-5 md:p-6">
					<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
						<div>
							<h1 className="font-Vazir-800 text-[22px] text-darkBlue md:text-[24px]">
								مدیریت وبینارها
							</h1>
							<p className="mt-1 font-Vazir-400 text-[13px] text-darkBlue/55">
								{listSummary}
								{appliedQuery ? ` · جستجو: «${appliedQuery}»` : ""}
							</p>
						</div>
						<div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
							<form
								onSubmit={applyListSearch}
								className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center"
							>
								<input
									type="search"
									value={listQuery}
									onChange={(e) => setListQuery(e.target.value)}
									placeholder="جستجو در عنوان، توضیح یا درمانگر…"
									className={`${editorInputClassName} w-full sm:max-w-[260px]`}
								/>
								<button
									type="submit"
									disabled={isLoading}
									className="shrink-0 rounded-[10px] border border-darkBlue/20 px-4 py-2.5 font-Vazir-600 text-[13px] text-darkBlue disabled:opacity-60"
								>
									جستجو
								</button>
							</form>
							<Link
								to="/admin_webinar_add/"
								className="shrink-0 rounded-[12px] bg-cta px-5 py-2.5 text-center font-Vazir-700 text-[14px] text-white no-underline shadow-sm transition hover:bg-cta/90"
							>
								+ وبینار جدید
							</Link>
						</div>
					</div>

					{errorMessage && (
						<p className="mt-4 rounded-[10px] bg-red-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-[#cc0000]">
							{errorMessage}
						</p>
					)}

					<div
						className={`mt-4 grid gap-3 transition-opacity ${isLoading ? "opacity-60" : ""}`}
					>
						{webinars.map((webinar) => (
							<WebinarAdminListRow
								key={webinar.id}
								webinar={webinar}
								onRemoved={handleRemoved}
								onError={setErrorMessage}
							/>
						))}
						{!isLoading && webinars.length === 0 && !appliedQuery && (
							<CatalogEmptyState
								message="هنوز وبیناری ثبت نشده است."
								hint="روی «وبینار جدید» بزنید یا از فرم افزودن وبینار استفاده کنید."
								actionLabel="افزودن وبینار"
								onAction={() => navigate("/admin_webinar_add/")}
							/>
						)}
						{!isLoading && webinars.length === 0 && appliedQuery && (
							<p className="py-8 text-center font-Vazir-400 text-midGray">
								وبیناری با این جستجو پیدا نشد.
							</p>
						)}
					</div>

					{maxPageNum > 1 && (
						<div className="mt-6 flex justify-center pt-2">
							<Paginator
								currentPage={page}
								maxPageNum={maxPageNum}
								isLoading={isLoading}
								onChange={setPage}
								scrollToTop={false}
								scrollAnchorRef={listSectionRef}
							/>
						</div>
					)}
				</DetailAccentCard>
			</div>
		</section>
	);
}
